import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getApiBase, fetchJson } from '../../apiBase';

function api() {
  const base = `${getApiBase()}/admin`;
  return {
    list: (school) => `${base}/exams/${encodeURIComponent(school)}`,
    summary: (school, examId) => `${base}/exams/${encodeURIComponent(school)}/${encodeURIComponent(examId)}/summary`,
    classes: (school) => `${base}/classes/${encodeURIComponent(school)}`,
  };
}

export default function MockExamResultsPage({ schoolId, onError }) {
  const [loadingMockExams, setLoadingMockExams] = useState(false);
  const [mockExams, setMockExams] = useState([]);
  const [selectedMockExam, setSelectedMockExam] = useState(null);

  const [loadingMockExamSummary, setLoadingMockExamSummary] = useState(false);
  const [rows, setRows] = useState([]);

  const [loadingClasses, setLoadingClasses] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [q, setQ] = useState('');

  const apis = useMemo(() => api(), []);

  const loadMockExams = useCallback(async () => {
    if (!schoolId) return;
    setLoadingMockExams(true);
    try {
      const res = await fetchJson(apis.list(schoolId));
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      const items = (data.items || []).map(x => ({
        id: x.exam_id,
        label: `${x.exam_id}${x.exam_date ? ' • ' + new Date(x.exam_date).toLocaleDateString('tr-TR') : ''}`,
        date: x.exam_date || '',
        kind: x.exam_kind || '',
      }));
      items.sort((a, b) => (b.date || '').localeCompare(a.date || '') || a.id.localeCompare(b.id));
      setMockExams(items);
      // Auto-select most recent
      setSelectedMockExam(prev => prev || items[0] || null);
    } catch (_) {
      onError && onError('Sınav listesi yüklenemedi');
    } finally {
      setLoadingMockExams(false);
    }
  }, [schoolId, apis, onError]);

  const loadClasses = useCallback(async () => {
    if (!schoolId) return;
    setLoadingClasses(true);
    try {
      const res = await fetchJson(apis.classes(schoolId));
      if (res.ok) {
        const arr = await res.json();
        const names = Array.isArray(arr) ? arr.map(c => (c?.name || c)).filter(Boolean) : [];
        names.sort((a, b) => String(a).localeCompare(String(b), 'tr-TR'));
        setClasses(names);
      }
    } catch (_) { }
    finally { setLoadingClasses(false); }
  }, [schoolId, apis]);

  const loadMockExamSummary = useCallback(async (mockExam) => {
    if (!schoolId || !mockExam) return;
    setLoadingMockExamSummary(true);
    try {
      const res = await fetchJson(apis.summary(schoolId, mockExam.id));
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      const items = data.items || [];
      // Normalize numbers and names
      items.forEach(r => {
        if (typeof r.total_net === 'string') r.total_net = parseFloat(r.total_net.replace(',', '.'));
        if (typeof r.lgs_score === 'string') r.lgs_score = parseFloat(r.lgs_score.replace(',', '.'));
        if (typeof r.perc_overall === 'string') {
          const v = parseFloat(r.perc_overall.replace(',', '.'));
          r.perc_overall = Number.isFinite(v) ? v : r.perc_overall;
        }
        r.full_name = (r.full_name || `${r.name || ''} ${r.surname || ''}`).trim();
      });
      // default sort: by school_rank (asc), then total_net (desc), then name
      const toNum = (v) => (typeof v === 'number' ? v : (v == null ? NaN : Number(String(v).replace(',', '.'))));
      const rankVal = (x) => {
        const r = toNum(x.school_rank);
        return Number.isFinite(r) ? r : Number.MAX_SAFE_INTEGER;
      };
      const netVal = (x) => {
        const n = toNum(x.total_net);
        return Number.isFinite(n) ? n : -1e9;
      };
      items.sort((a, b) => (
        (rankVal(a) - rankVal(b)) ||
        (netVal(b) - netVal(a)) ||
        (a.full_name || '').localeCompare(b.full_name || '', 'tr-TR')
      ));
      setRows(items);
    } catch (_) {
      onError && onError('Sınav özeti yüklenemedi');
      setRows([]);
    } finally {
      setLoadingMockExamSummary(false);
    }
  }, [schoolId, apis, onError]);

  useEffect(() => { loadMockExams(); }, [loadMockExams]);
  useEffect(() => { loadClasses(); }, [loadClasses]);
  useEffect(() => { if (selectedMockExam) loadMockExamSummary(selectedMockExam); }, [selectedMockExam, loadMockExamSummary]);

  const filteredRows = useMemo(() => {
    let r = rows;
    if (selectedClass) {
      r = r.filter(x => String(x.class_name || '') === String(selectedClass));
    }
    if (q.trim()) {
      const qq = q.trim().toLowerCase();
      r = r.filter(x => (x.full_name || '').toLowerCase().includes(qq) || String(x.student_id || '').includes(qq));
    }
    return r;
  }, [rows, selectedClass, q]);

  const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
      <Typography variant="body2">{message}</Typography>
    </Box>
  );

  return (
    <Box>
      {/* Top bar: Title + controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Sınav Sonuçları</Typography>
          <Typography variant="caption" color="text.secondary">Şube ve ada göre filtreleyin, en yüksek netler üstte</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Yenile">
            <span>
              <IconButton onClick={() => { loadMockExams(); if (selectedMockExam) loadMockExamSummary(selectedMockExam); }} disabled={loadingMockExams || loadingMockExamSummary}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Autocomplete
          size="small"
          sx={{ minWidth: 260 }}
          options={mockExams}
          getOptionLabel={(opt) => opt?.label || ''}
          value={selectedMockExam}
          onChange={(_, v) => setSelectedMockExam(v)}
          loading={loadingMockExams}
          renderInput={(params) => (
            <TextField {...params} label="Sınav" placeholder="Seç / yaz" InputProps={{ ...params.InputProps, endAdornment: (
              <>
                {loadingMockExams ? <Skeleton variant="circular" width={16} height={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ) }} />
          )}
          clearOnEscape
        />
        <Autocomplete
          size="small"
          sx={{ minWidth: 240 }}
          options={classes}
          getOptionLabel={(opt) => opt}
          value={selectedClass || null}
          onChange={(_, v) => setSelectedClass(v || '')}
          loading={loadingClasses}
          renderInput={(params) => (
            <TextField {...params} label="Şube" placeholder="Seç / yaz" InputProps={{ ...params.InputProps, endAdornment: (
              <>
                {loadingClasses ? <Skeleton variant="circular" width={16} height={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ) }} />
          )}
          clearOnEscape
        />
        <TextField size="small" label="Ara (ad, numara)" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 260 }} />
      </Box>

      {/* Content area */}
      <Paper sx={{ p: 0, borderRadius: 2 }}>
        {loadingMockExamSummary ? (
          <Box sx={{ p: 2 }}>
            {[...Array(6)].map((_, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.25 }}>
                <Skeleton variant="rectangular" width="100%" height={44} sx={{ borderRadius: 1 }} />
              </Box>
            ))}
          </Box>
        ) : (!selectedMockExam ? (
          <EmptyState message="Önce bir sınav seçin" />
        ) : (filteredRows.length === 0 ? (
          <EmptyState message="Henüz sonuç bulunmuyor" />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ad Soyad</TableCell>
                  <TableCell>Şube</TableCell>
                  <TableCell align="right">Toplam Net</TableCell>
                  <TableCell align="right">Doğru</TableCell>
                  <TableCell align="right">Yanlış</TableCell>
                  <TableCell align="right">Boş</TableCell>
                  <TableCell align="right">LGS Puanı</TableCell>
                  <TableCell align="right">Şube Sırası</TableCell>
                  <TableCell align="right">Okul Sırası</TableCell>
                  <TableCell align="right">İlçe Sırası</TableCell>
                  <TableCell align="right">İl Sırası</TableCell>
                  <TableCell align="right">Genel Sıra</TableCell>
                  <TableCell align="right">Yüzdelik Dilim</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((r) => (
                  <TableRow key={r.student_id || r.full_name} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.full_name || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">#{r.student_id || ''}</Typography>
                    </TableCell>
                    <TableCell>{r.class_name || '-'}</TableCell>
                    <TableCell align="right">{typeof r.total_net === 'number' ? r.total_net.toFixed(2) : '-'}</TableCell>
                    <TableCell align="right">{r.total_correct ?? '-'}</TableCell>
                    <TableCell align="right">{r.total_wrong ?? '-'}</TableCell>
                    <TableCell align="right">{r.total_blank ?? '-'}</TableCell>
                    <TableCell align="right">{typeof r.lgs_score === 'number' ? r.lgs_score.toFixed(2) : '-'}</TableCell>
                    <TableCell align="right">{r.class_rank ?? '-'}</TableCell>
                    <TableCell align="right">{r.school_rank ?? '-'}</TableCell>
                    <TableCell align="right">{r.district_rank ?? '-'}</TableCell>
                    <TableCell align="right">{r.province_rank ?? '-'}</TableCell>
                    <TableCell align="right">{r.general_rank ?? '-'}</TableCell>
                    <TableCell align="right">{typeof r.perc_overall === 'number' ? r.perc_overall.toFixed(2) : (r.perc_overall ?? '-')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ))) }
      </Paper>
    </Box>
  );
}
